import { useState } from 'react'
import {
  Modal,
  Button,
  Typography,
  Flex,
  Space,
  Tag,
  Divider,
  Alert,
} from 'antd'
import { RollbackOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'

import { submitRollback, rollbackTransactionRequest } from './model'
import {
  ReasonAutoComplete,
  commitReason,
} from '../../../../../shared/reasonAutocomplete'
import { transactionRequestDestinationType } from '../../../const'
import { WithdrawalEdit } from '../../edit'

const { Text } = Typography

type Props = {
  request: WithdrawalEdit
  className?: string
  color?: string
  disabled?: boolean
}

function MetaItem({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <Flex align="baseline" gap={4}>
      <Text
        type="secondary"
        style={{ fontSize: 14, whiteSpace: 'nowrap' }}
      >
        {label}:
      </Text>
      <Text style={{ fontSize: 16 }}>
        {typeof value === 'boolean'
          ? value
            ? 'Yes'
            : 'No'
          : value}
      </Text>
    </Flex>
  )
}

export const RollbackModal = ({
  request,
  className,
  disabled,
}: Props) => {
  const isLoading = useStore(rollbackTransactionRequest.pending)
  const [isVisible, setVisible] = useState(false)
  const [rollbackText, setRollbackText] = useState('')
  const [error, setError] = useState('')

  const handleClose = () => {
    setVisible(false)
    setRollbackText('')
    setError('')
  }

  const handleSubmit = () => {
    if (!rollbackText.trim()) {
      setError('Please enter a reason')
      return
    }
    commitReason('withdrawal-rollback-reason', rollbackText)
    submitRollback({
      requestId: request.transactionId,
      rejectReason: rollbackText,
    })
    handleClose()
  }

  const purseLabel =
    request.destinationType ===
    transactionRequestDestinationType.wallet()
      ? 'Wallet ID'
      : 'Account ID'

  return (
    <>
      <Button
        type="primary"
        className={className}
        onClick={() => setVisible(true)}
        disabled={disabled}
      >
        Rollback
      </Button>
      <Modal
        open={isVisible}
        onCancel={handleClose}
        title={
          <Flex align="center" gap={8}>
            <RollbackOutlined style={{ color: '#6366f1' }} />
            <span>Set rollback reason</span>
          </Flex>
        }
        width={900}
        footer={
          <Flex justify="space-between" align="center">
            <Text type="secondary" style={{ fontSize: 11 }}>
              {rollbackText.trim()
                ? 'Review the reason before sending'
                : 'Enter a reason to continue'}
            </Text>
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                disabled={!rollbackText.trim() || isLoading}
                loading={isLoading}
                icon={<RollbackOutlined />}
                onClick={handleSubmit}
              >
                Rollback
              </Button>
            </Space>
          </Flex>
        }
        styles={{
          body: {
            padding: '16px 24px',
            maxHeight: '70vh',
            overflowY: 'auto',
          },
          header: { paddingBottom: 12 },
        }}
      >
        {/* Transaction meta */}
        <div
          style={{
            background: 'var(--crm-bg-elevated)',
            border: '1px solid var(--crm-border)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
          }}
        >
          <Flex gap={24} style={{ marginBottom: 8 }}>
            <Flex gap={4} align="baseline">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Created
              </Text>
              <Text style={{ fontSize: 16 }}>
                {dayjs(request.createdAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Flex>
            <Flex gap={4} align="baseline">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Updated
              </Text>
              <Text style={{ fontSize: 16 }}>
                {dayjs(request.updatedAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Flex>
          </Flex>

          <Divider style={{ margin: '8px 0' }} />

          <Flex gap={20} wrap="wrap">
            <MetaItem label="ID" value={request.transactionId} />
            <MetaItem
              label="Client ID"
              value={request.client.clientId}
            />
            <MetaItem label={purseLabel} value={request.destinationId} />
            <MetaItem
              label="Processing type"
              value={
                <Tag
                  color={request.isAuto ? 'green' : 'geekblue'}
                  style={{ fontSize: 11 }}
                >
                  {request.isAuto ? 'Auto' : 'Manual'}
                </Tag>
              }
            />
          </Flex>

          <Divider style={{ margin: '8px 0' }} />
          <Flex gap={16} wrap="wrap">
            <MetaItem
              label="clientPurseId"
              value={request.clientPurseId}
            />
            {request.clientPurseId != 0 && (
              <>
                <MetaItem
                  label="isCard"
                  value={Boolean(
                    request.clientPurse?.isCard,
                  ).toString()}
                />
                <MetaItem
                  label="expiry"
                  value={request.clientPurse?.expiry}
                />
                <MetaItem
                  label="mask"
                  value={request.clientPurse?.mask}
                />
                <MetaItem
                  label="type"
                  value={request.clientPurse?.type}
                />
              </>
            )}
          </Flex>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            style={{ marginBottom: 12 }}
          />
        )}

        <Text
          type="secondary"
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'block',
            marginBottom: 10,
          }}
        >
          Rollback reason
        </Text>

        <ReasonAutoComplete
          scope="withdrawal-rollback-reason"
          multiline
          rows={3}
          placeholder="Enter rollback reason..."
          value={rollbackText}
          onChange={(val) => {
            setRollbackText(val)
            setError('')
          }}
          style={{ fontFamily: 'monospace' }}
        />
      </Modal>
    </>
  )
}
